import { isDevPreview } from '../boot/devPreview';
import { sb } from './supabase';

/**
 * ONE 플랫폼 알림 발송 (통합 문서 7절).
 *
 * ★ 반드시 `push_notification()` RPC 를 거친다 — 공용 `notifications` 테이블에
 *   직접 insert 하면 RLS 가 막는다. 이 테이블·RPC 는 쉘 쪽 코드가 만든 것으로,
 *   이 저장소에서는 만들지 않는다.
 *
 * ★ `source_system` 은 쉘의 `menu_items.system_key` 와 반드시 같아야 한다 —
 *   다르면 알림은 가지만 클릭했을 때 쉘이 이 앱으로 자동 이동을 못 시킨다.
 *   이 값은 쉘 관리자가 확인해 준 값('cad')이다.
 *
 * ★ 알림은 부가 기능이다. 실패해도 댓글 작성·승인 같은 원래 동작은
 *   절대 막지 않는다 — 실패는 콘솔에만 남긴다.
 */
const SYSTEM_KEY = 'cad';

export interface NotifyInput {
  toEmail: string;
  title: string;
  body: string;
  /** `toHash()` 가 만든, `#` 으로 시작하는 이 앱 내부 해시 경로 */
  hashPath: string;
}

export async function pushNotification(input: NotifyInput): Promise<void> {
  if (isDevPreview()) return;
  try {
    const client = sb();
    const { error } = await client.rpc('push_notification', {
      p_user_email: input.toEmail,
      p_source: SYSTEM_KEY,
      p_title: input.title,
      p_body: input.body,
      p_link: `/${SYSTEM_KEY}/${input.hashPath}`,
    });
    if (error) console.warn(`알림 전송 실패 (${input.toEmail}): ${error.message}`);
  } catch (e) {
    console.warn('알림 전송 실패:', e);
  }
}

/**
 * 여러 명에게 "같은 내용"을 한 번에 보낸다.
 *
 * ★ 반드시 `push_notifications_bulk()` RPC 로 한 번에 넣는다 — 사람마다 RPC 를
 *   따로 호출하면 INSERT 문이 여러 개가 되어, 쉘의 앱 푸시 트리거가 사람 수만큼
 *   store261 API 를 호출하게 된다(원플랫폼 통합 문서 7.5). 여기서 한 번에 넣어야
 *   쉘이 사번을 모아 store261 API 를 1번만 부른다.
 *
 * make(toEmails[0]) 의 title/body/hashPath 를 대표값으로 쓴다 — 여러 명에게 보내는
 * 알림은 애초에 사람마다 내용이 달라질 이유가 없다(다르다면 이 함수 대상이 아니다).
 */
export async function pushNotifications(
  toEmails: string[],
  make: (email: string) => NotifyInput
): Promise<void> {
  const emails = [...new Set(toEmails)];
  const firstEmail = emails[0];
  if (!firstEmail) return;
  if (isDevPreview()) return;
  const sample = make(firstEmail);
  try {
    const client = sb();
    const { error } = await client.rpc('push_notifications_bulk', {
      p_user_emails: emails,
      p_source: SYSTEM_KEY,
      p_title: sample.title,
      p_body: sample.body,
      p_link: `/${SYSTEM_KEY}/${sample.hashPath}`,
    });
    if (error) console.warn(`알림 일괄 전송 실패 (${emails.length}명): ${error.message}`);
  } catch (e) {
    console.warn('알림 일괄 전송 실패:', e);
  }
}
